import { ApiClient } from '@api/api-configuration';
import { useModal } from '@context/modal-provider';
import type { ModalInstanceApi } from '@context/modal-provider/types';
import { message, Tooltip } from 'antd';
import { FormikProps } from 'formik';
import React, { useCallback, useRef } from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import IssueReportModal, { IssueReportResult } from './IssueReportModal';

const IssueReportTrigger: React.FC = () => {
  const { t } = useTranslation();
  const { openForm } = useModal();
  const formRef = useRef<FormikProps<IssueReportResult>>(null);

  const handleOpen = useCallback(async () => {
    try {
      openForm(
        IssueReportModal,
        { formRef },
        {
          modalProps: {
            title: t('issue-reporting-modal-title'),
            width: 800,
            destroyOnHidden: true,
            footer: null,
          },
          autoClose: { ok: false, cancel: false },
          onSubmit: async (values: IssueReportResult, api: ModalInstanceApi<IssueReportResult>) => {
            try {
              api.setOkLoading(true);

              const priorityMap: Record<'high' | 'medium' | 'low', number> = {
                high: 2,
                medium: 3,
                low: 4,
              };

              // Prepare the FormData for the ClickUp ticket
              const formData = new FormData();
              formData.append('title', values.title);
              formData.append('description', values.description);
              formData.append('priority', (priorityMap[values.priority] ?? 3).toString());

              // If there's a screenshot, convert it to a File object and attach it
              if (values.fileBase64 && values.filename) {
                const base64Data = values.fileBase64;
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'image/png' });
                const file = new File([blob], values.filename, { type: 'image/png' });
                formData.append('files', file);
              }

              // Add additional attachments to the FormData
              if (values.attachments && values.attachments.length > 0) {
                values.attachments.forEach((attachment) => {
                  formData.append('files', attachment);
                });
              }

              // Call the ClickUp ticket API with all attachments
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              await ApiClient.usersApi.tickets.createClickUpTicket(formData as any);

              message.success(t('issue-reporting-submit-success'));
              api.resolve(values);
            } catch (err) {
              console.error('Error submitting issue report', err);
              message.error(t('issue-reporting-submit-error'));
              api.reject(err);
            } finally {
              api.setOkLoading(false);
            }
          },
          onCancel: async (ctx, api) => {
            api.close();
          },
        }
      );
    } catch (error) {
      console.error('Issue report dialog failed', error);
      message.error(t('issue-reporting-open-error'));
    }
  }, [openForm, formRef, t]);

  return (
    <Tooltip title={t('issue-reporting-trigger-tooltip')}>
      <button
        type="button"
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-label={t('issue-reporting-trigger-aria')}
        className="relative inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-gray-100"
      >
        <FiAlertCircle className="h-5 w-5" />
      </button>
    </Tooltip>
  );
};

export default IssueReportTrigger;
